import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle2, Calculator } from 'lucide-react';
import { submitEnquiry } from '../lib/enquiries';

/**
 * EstimatePopup — global modal for "Get Estimate" CTAs.
 * Mounted once at the app root. Listens for the `guardian:open-estimate`
 * custom event to open from anywhere (Navbar GET IN TOUCH, sector cards,
 * floating button, etc).
 *
 * Self-contained: own form state, axios POST to /api/enquiries, success state.
 */
export default function EstimatePopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', kind: 'Residential', size: '50—100m²', postcode: '' });

  useEffect(() => {
    const handler = () => { setOpen(true); setDone(false); setErr(''); };
    window.addEventListener('guardian:open-estimate', handler);
    return () => window.removeEventListener('guardian:open-estimate', handler);
  }, []);

  useEffect(() => {
    // Lock body scroll while modal is open
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    // ESC to close
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    const kindMap = { Residential: 'residential', Commercial: 'commercial', Sports: 'sports', Education: 'education', Other: 'other' };
    try {
      await submitEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        postcode: form.postcode.trim() || null,
        project_type: kindMap[form.kind] || 'residential',
        message: `Approx. size: ${form.size}`,
        source: 'estimate-box',
      });
      setDone(true);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x.msg).join(' · ') : 'Something went wrong. Please call us on 01634 557852.');
    } finally {
      setSubmitting(false);
    }
  };

  const kinds = ['Residential', 'Commercial', 'Sports', 'Education', 'Other'];
  const sizes = ['Under 50m²', '50—100m²', '100—250m²', '250m²+'];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[90] bg-[#162A1E]/80 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={close}
          data-testid="estimate-popup"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-gradient-to-b from-[#1C3623] to-[#162A1E] border border-[#6A8F3E]/40 rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={close}
              aria-label="Close estimate form"
              data-testid="estimate-popup-close"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0F1A14]/60 border border-[#E7E9E4]/30 hover:border-[#6A8F3E] hover:bg-[#6A8F3E] flex items-center justify-center text-[#E7E9E4] transition-all z-10"
            >
              <X size={16} strokeWidth={2.4} />
            </button>

            <div className="p-7 md:p-10">
              {done ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#6A8F3E]/20 border border-[#6A8F3E]/50 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={28} className="text-[#6A8F3E]" />
                  </div>
                  <h3 className="font-serif-editorial text-3xl md:text-4xl tracking-tight mb-3 text-[#E7E9E4]">Thank you.</h3>
                  <p className="text-[#E7E9E4]/85 leading-relaxed max-w-md mx-auto mb-7">
                    A specialist will follow up within 24 hours to arrange your free site survey.
                  </p>
                  <button
                    onClick={close}
                    data-cursor="hover"
                    className="text-[11px] tracking-[0.3em] uppercase text-[#6A8F3E] font-semibold link-underline"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#A3C66F] font-bold mb-1">
                    <Calculator size={13} strokeWidth={2.4} /> Free Estimate
                  </div>
                  <div>
                    <h3 className="font-serif-editorial text-3xl md:text-4xl leading-tight tracking-tight text-[#E7E9E4]">
                      Get a no-obligation quote in <span className="italic text-[#A3C66F]">90 seconds.</span>
                    </h3>
                    <p className="text-sm text-[#E7E9E4]/90 mt-2">A specialist will follow up within 24 hours.</p>
                  </div>

                  {/* Project type */}
                  <div>
                    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3C66F] font-bold block mb-2.5">Project type</label>
                    <div className="flex flex-wrap gap-2">
                      {kinds.map(k => (
                        <button
                          type="button"
                          key={k}
                          onClick={() => setForm({ ...form, kind: k })}
                          data-cursor="hover"
                          className={`text-[11px] tracking-[0.22em] uppercase font-bold px-4 py-2 rounded-full border transition-all ${form.kind === k ? 'bg-[#6A8F3E] border-[#6A8F3E] text-[#0F1A14] shadow-[0_4px_14px_rgba(106,143,62,0.45)]' : 'bg-[#0F1A14]/40 border-[#E7E9E4]/30 text-[#E7E9E4] hover:border-[#6A8F3E] hover:bg-[#6A8F3E]/15'}`}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Size */}
                  <div>
                    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3C66F] font-bold block mb-2.5">Approx. size</label>
                    <div className="flex flex-wrap gap-2">
                      {sizes.map(s => (
                        <button
                          type="button"
                          key={s}
                          onClick={() => setForm({ ...form, size: s })}
                          data-cursor="hover"
                          className={`text-[11px] tracking-[0.22em] uppercase font-bold px-4 py-2 rounded-full border transition-all ${form.size === s ? 'bg-[#6A8F3E] border-[#6A8F3E] text-[#0F1A14] shadow-[0_4px_14px_rgba(106,143,62,0.45)]' : 'bg-[#0F1A14]/40 border-[#E7E9E4]/30 text-[#E7E9E4] hover:border-[#6A8F3E] hover:bg-[#6A8F3E]/15'}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className="bg-[#0F1A14]/60 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/80 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/60 transition-all" data-testid="estimate-popup-name" />
                    <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" className="bg-[#0F1A14]/60 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/80 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/60 transition-all" data-testid="estimate-popup-phone" />
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="bg-[#0F1A14]/60 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/80 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/60 transition-all" data-testid="estimate-popup-email" />
                    <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode (optional)" className="bg-[#0F1A14]/60 border border-[#E7E9E4]/25 focus:border-[#6A8F3E] focus:bg-[#0F1A14]/80 outline-none rounded-xl px-4 py-3 text-sm text-[#E7E9E4] placeholder:text-[#E7E9E4]/60 transition-all" />
                  </div>

                  {err && (
                    <div className="text-[12px] text-red-100 bg-red-500/20 border border-red-400/45 rounded-lg px-4 py-3" data-testid="estimate-popup-error">
                      {err}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-cursor="hover"
                    data-testid="estimate-popup-submit"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] px-7 py-4 rounded-full transition-all disabled:opacity-60 font-extrabold shadow-[0_8px_28px_rgba(106,143,62,0.45)]"
                  >
                    <span className="text-[11px] tracking-[0.3em] uppercase">
                      {submitting ? 'Sending…' : 'Send my enquiry'}
                    </span>
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" strokeWidth={2.6} />
                  </button>

                  <p className="text-[11px] text-center text-[#E7E9E4]/75 leading-relaxed">
                    By submitting you agree to be contacted by Guardian about your enquiry. We never share your details.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Programmatic helper — call from anywhere via `openEstimate()`. */
export function openEstimate() {
  window.dispatchEvent(new CustomEvent('guardian:open-estimate'));
}
